/**
 * Utility functions for subscription management
 */

/**
 * Check if a subscription status indicates an active subscription
 * @param {string} status - Subscription status from userProfile or subscription
 * @returns {boolean} - True if subscription is active
 */
export const isActiveSubscription = (status) => {
  if (!status) return false;
  const normalizedStatus = status?.toLowerCase();
  return ['active', 'premium', 'trialing'].includes(normalizedStatus);
};

/**
 * Get subscription status from user profile
 * @param {object} userProfile - User profile object
 * @returns {string} - Subscription status
 */
export const getSubscriptionStatus = (userProfile) => {
  return userProfile?.subscriptionStatus || 'free';
};

/**
 * Check if user has active subscription from profile
 * @param {object} userProfile - User profile object
 * @returns {boolean} - True if user has active subscription
 */
export const hasActiveSubscription = (userProfile) => {
  // Female users get free access (no subscription required)
  if (userProfile?.gender?.toLowerCase() === 'female') {
    return true;
  }
  
  // Demo accounts marked as premium for testing
  const demoEmails = [
    'sarah.m@example.com',
    'priya.k@example.com',
    'anjali.s@example.com',
    'neha.r@example.com',
    'anonymous.female@example.com',
    'raj.kumar@example.com',
    'test.user@example.com'
  ];
  
  if (userProfile?.email && demoEmails.includes(userProfile.email.toLowerCase())) {
    return true;
  }
  
  return isActiveSubscription(getSubscriptionStatus(userProfile));
};

/**
 * Check if verification is approved (for showing verified badges)
 * @param {object} userProfile - User profile object
 * @param {string} type - 'photo' or 'identity'
 * @returns {boolean} - True if verification is approved
 */
export const isVerificationApproved = (userProfile, type) => {
  if (type === 'photo') {
    return userProfile?.photoVerificationStatus === 'approved' || userProfile?.photoVerified === true;
  }
  if (type === 'identity') {
    return userProfile?.identityVerificationStatus === 'approved' || userProfile?.identityVerified === true;
  }
  return false;
};

export default {
  isActiveSubscription,
  getSubscriptionStatus,
  hasActiveSubscription
};
